// LeaveManagement.js - Fixed: Departmental leaves + Read More Modal Added
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ManagerLeaveDashboard.css';

const LeaveManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [userDepartment, setUserDepartment] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentName, setCurrentName] = useState('');
  const [academicYear] = useState('2025 - 2026');

  // Action Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState('');
  const [currentLeaveId, setCurrentLeaveId] = useState(null);
  const [reason, setReason] = useState('');

  // New: Full Reason Modal States
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const api = axios.create({
    baseURL: 'https://backend.ems.hiradentallab.com/api/leavesmanual',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const department = localStorage.getItem('department');
    const userId = localStorage.getItem('userId');
    const name = localStorage.getItem('name');

    if (!token || !department || !userId) {
      toast.error('Authentication details missing. Please log in again.');
      setLoading(false);
      return;
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUserDepartment(department.trim());
    setCurrentUserId(parseInt(userId));
    setCurrentName(name || 'Manager');

    fetchDepartmentLeaves(department.trim(), parseInt(userId));
  }, []);

  const fetchDepartmentLeaves = async (department, userId) => {
    setLoading(true);
    try {
      const { data } = await api.get('/all-leaves', {
        params: { 
          status: 'all', 
          page: 1, 
          limit: 100,
          department: department
        }
      });

      if (data.success) {
        let allLeaves = data.allLeaves || data.leaves || [];

        const departmentLeaves = allLeaves.filter(leave => {
          const leaveDept = (leave.department || '').trim().toLowerCase();
          const myDept = department.toLowerCase();
          return leaveDept === myDept && leave.userId !== userId;
        });

        setLeaves(departmentLeaves);

        const pending = departmentLeaves.filter(l => l.status === 'pending').length;
        const approved = departmentLeaves.filter(l => l.status === 'approved').length;
        const rejected = departmentLeaves.filter(l => l.status === 'rejected').length;

        setStats({
          all: departmentLeaves.length,
          pending,
          approved,
          rejected
        });

        if (departmentLeaves.length === 0) {
          toast.info("No leaves found in your department yet.");
        }
      } else {
        toast.error('Failed to fetch leaves: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error('Error fetching leaves: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredLeaves = leaves.filter(leave => {
    const matchesTab = activeTab === 'all' || leave.status === activeTab;
    const matchesSearch = searchQuery === '' ||
      (leave.reason?.toLowerCase() || '').includes(searchQuery) ||
      (leave.name?.toLowerCase() || '').includes(searchQuery) ||
      (leave.allowanceType?.toLowerCase() || '').includes(searchQuery);

    return matchesTab && matchesSearch;
  });

  const handleOpenModal = (leaveId, action) => {
    setCurrentLeaveId(leaveId);
    setCurrentAction(action);
    setReason('');
    setIsModalOpen(true);
  };

  // New: View Full Reason Function
  const handleViewFullReason = (reasonText, employeeName) => {
    setSelectedReason(reasonText || 'No reason provided');
    setSelectedEmployee(employeeName || 'Unknown Employee');
    setIsReasonModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (currentAction === 'rejected' && !reason.trim()) {
      toast.error('Reason is required when rejecting a leave!');
      return;
    }

    try {
      const payload = {
        action: currentAction,
        role: 'manager',
        notes: reason.trim()
      };

      const { data } = await api.put(`/${currentLeaveId}/status`, payload);

      if (data.success) {
        toast.success(`Leave ${currentAction}d successfully!`);
        fetchDepartmentLeaves(userDepartment, currentUserId);
      } else {
        toast.error(data.message || 'Action failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong.');
    } finally {
      setIsModalOpen(false);
      setReason('');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="manager-leave-dashboard">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Action Modal (Approve/Reject) */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{currentAction === 'approved' ? 'Approve Leave' : 'Reject Leave'}</h2>
            <p>
              Please provide your decision notes/reason<br />
              <small>(Required for rejection, optional for approval)</small>
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              className="modal-textarea"
              placeholder="Enter your notes / reason here..."
            />
            <div className="modal-buttons">
              <button
                className={`modal-btn-confirm ${currentAction === 'rejected' ? 'modal-btn-danger' : ''}`}
                onClick={handleConfirmAction}
              >
                {currentAction === 'approved' ? 'Approve' : 'Reject'}
              </button>
              <button className="modal-btn-cancel" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New: Full Reason Modal */}
      {isReasonModalOpen && (
        <div className="modal-overlay" onClick={() => setIsReasonModalOpen(false)}>
          <div className="modal-content reason-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Full Leave Reason</h2>
            
            <div className="employee-info">
              <strong>Employee:</strong> {selectedEmployee}
            </div>

            <div className="full-reason-box">
              <p>{selectedReason}</p>
            </div>

            <div className="modal-buttons">
              <button 
                className="modal-btn-cancel"
                onClick={() => setIsReasonModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="page-title">
        <h1>Departmental Leave Management Dashboard</h1>
        <p>Track and manage your leave records throughout the academic year</p>
      </div>

      {/* Welcome Header Section */}
      <div className="manager-header-section">
        <div className="user-header">
          <div className="welcome-message">
            <h2 className="user-name-display">Welcome, {currentName}!</h2>
            <span className="welcome-text">Department: {userDepartment}</span>
          </div>

          <div className="date-navigation">
            <div className="academic-year">{academicYear}</div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="manager-leave-summary">
        <div className="summary-card">
          <h3>📋 TOTAL LEAVES</h3>
          <div className="allowance-display">
            <div className="allowance-number">{stats.all}</div>
          </div>
        </div>

        <div className="summary-card">
          <h3>⏳ PENDING</h3>
          <div className="allowance-display">
            <div className="allowance-number">{stats.pending}</div>
          </div>
        </div>

        <div className="summary-card">
          <h3>✅ APPROVED</h3>
          <div className="allowance-display">
            <div className="allowance-number">{stats.approved}</div>
          </div>
        </div>

        <div className="summary-card">
          <h3>❌ REJECTED</h3>
          <div className="allowance-display">
            <div className="allowance-number">{stats.rejected}</div>
          </div>
        </div>
      </div>

      {/* Leave Records Section */}
      <div className="manager-table-container">
        <div className="manager-table-header">
          <div className="manager-header-left">
            <h3>Leave Records</h3>
            <p className="manager-record-count">{filteredLeaves.length} records found</p>
          </div>

          <div className="manager-header-right">
            <div className="manager-search-box">
              <div className="manager-search-icon">🔍</div>
              <input
                type="text"
                placeholder="Search by name, reason"
                value={searchQuery}
                onChange={handleSearch}
                className="manager-search-input"
              />
            </div>

            <div className="manager-action-buttons">
              <button className="manager-filter-btn">
                <span className="btn-icon">⚙️</span>
                Filter
              </button>
              <button className="manager-export-btn">
                <span className="btn-icon">📥</span>
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="manager-tabs-container">
          <div className="manager-tabs">
            <button className={`manager-tab ${activeTab === 'all' ? 'manager-tab-active' : ''}`} onClick={() => setActiveTab('all')}>
              All Leaves <span className="manager-tab-count">{stats.all}</span>
            </button>
            <button className={`manager-tab ${activeTab === 'pending' ? 'manager-tab-active' : ''}`} onClick={() => setActiveTab('pending')}>
              Pending <span className="manager-tab-count">{stats.pending}</span>
            </button>
            <button className={`manager-tab ${activeTab === 'approved' ? 'manager-tab-active' : ''}`} onClick={() => setActiveTab('approved')}>
              Approved <span className="manager-tab-count">{stats.approved}</span>
            </button>
            <button className={`manager-tab ${activeTab === 'rejected' ? 'manager-tab-active' : ''}`} onClick={() => setActiveTab('rejected')}>
              Rejected <span className="manager-tab-count">{stats.rejected}</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="manager-table-wrapper">
          {loading ? (
            <div className="manager-loading">
              <div className="manager-spinner"></div>
              <p>Loading leave records...</p>
            </div>
          ) : filteredLeaves.length > 0 ? (
            <div className="manager-table-responsive">
              <table className="manager-leave-table">
                <thead>
                  <tr>
                    <th>START DATE</th>
                    <th>END DATE</th>
                    <th>ALLOWANCE TYPE</th>
                    <th>REASON</th>
                    <th>EMPLOYEE</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaves.map((leave) => (
                    <tr key={leave._id}>
                      <td>
                        <div className="manager-date-cell">
                          <span className="date-icon">📅</span>
                          <span>{formatDate(leave.startDate)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="manager-date-cell">
                          <span className="date-icon">📅</span>
                          <span>{formatDate(leave.endDate)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="manager-type-tag">
                          {leave.allowanceType?.charAt(0).toUpperCase() + leave.allowanceType?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="manager-reason-cell">
                        <span className="manager-reason-text">
                          {leave.reason || 'No reason provided'}
                        </span>
                        {leave.reason && leave.reason.length > 50 && (
                          <button 
                            className="manager-view-more-btn"
                            onClick={() => handleViewFullReason(leave.reason, leave.name)}
                          >
                            [View More]
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="manager-employee-cell">
                          <div className="manager-employee-avatar">
                            {leave.name?.charAt(0) || 'U'}
                          </div>
                          <div className="manager-employee-info">
                            <div className="manager-employee-name">{leave.name || 'Unknown'}</div>
                            <div className="manager-employee-dept">{userDepartment}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={`manager-status-tag manager-status-${leave.status}`}>
                          <span className="manager-status-icon">
                            {leave.status === 'pending' && '⏳'}
                            {leave.status === 'approved' && '✅'}
                            {leave.status === 'rejected' && '❌'}
                          </span>
                          <span>{leave.status?.toUpperCase() || 'UNKNOWN'}</span>
                        </div>
                      </td>
                      <td>
                        {leave.status === 'pending' ? (
                          <div className="manager-action-buttons">
                            <button
                              className="manager-btn-approve"
                              onClick={() => handleOpenModal(leave._id, 'approved')}
                            >
                              Approve
                            </button>
                            <button
                              className="manager-btn-reject"
                              onClick={() => handleOpenModal(leave._id, 'rejected')}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <div className="manager-status-text">
                            <span className={`manager-text-${leave.status}`}>
                              {leave.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="manager-no-data">
              <div className="manager-empty-icon">📊</div>
              <h3>No leave records found</h3>
              <p>
                {activeTab === 'all'
                  ? "Your department has no leave requests yet."
                  : `No ${activeTab} leaves in your department at the moment.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveManagement;